import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Switch,
  message,
  Space,
  Tag,
  Popconfirm,
  Row,
  Col,
  Typography,
  Badge,
  Tooltip,
  Tabs,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  LockOutlined,
  UnorderedListOutlined,
  UserAddOutlined,
  UsbOutlined,
} from "@ant-design/icons";
import { useAdminPermissions } from "../context/AdminContext";

const { Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("list");
  const [selectedAdminForPassword, setSelectedAdminForPassword] =
    useState(null);

  // États pour le formulaire de création
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("");
  const [isActive, setIsActive] = useState(true);

  // États pour le formulaire de modification
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editFullname, setEditFullname] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editIsActive, setEditIsActive] = useState(true);

  // États pour le changement de mot de passe
  const [passwordAdminId, setPasswordAdminId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const { authToken, admin: currentAdmin } = useAdminPermissions();
  const API_BASE_URL = process.env.REACT_APP_API_URL || "";

  const isSuperAdmin = currentAdmin?.role === "super_admin";

  const roles = [
    { value: "super_admin", label: "Super Admin", color: "red" },
    { value: "admin_global", label: "Admin Global", color: "orange" },
    { value: "manager_financier", label: "Manager Financier", color: "blue" },
    {
      value: "manager_utilisateurs",
      label: "Manager Utilisateurs",
      color: "green",
    },
    { value: "support_analyste", label: "Support Analyste", color: "cyan" },
    { value: "agent_commercial", label: "Agent Commercial", color: "purple" },
  ];

  const getRoleColor = (role) => {
    const roleObj = roles.find((r) => r.value === role);
    return roleObj ? roleObj.color : "default";
  };

  const getRoleLabel = (role) => {
    const roleObj = roles.find((r) => r.value === role);
    return roleObj ? roleObj.label : role;
  };

  const fetchAdmins = async () => {
    if (!isSuperAdmin) {
      message.error(
        "Accès refusé. Seul le super admin peut accéder à cette page."
      );
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-management/all`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Erreur lors du chargement");
      }

      const data = await response.json();

      // ✅ Correction : les admins sont dans data.data
      setAdmins(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAdmins();
    }
  }, [isSuperAdmin]);

  const resetCreateForm = () => {
    setFullname("");
    setEmail("");
    setPhone("");
    setRole("");
    setPassword("");
    setIsActive(true);
  };

  const resetEditForm = () => {
    setEditingAdmin(null);
    setEditFullname("");
    setEditEmail("");
    setEditPhone("");
    setEditRole("");
    setEditIsActive(true);
  };

  const resetPasswordForm = () => {
    setPasswordAdminId("");
    setNewPassword("");
    setConfirmPassword("");
    setSelectedAdminForPassword(null);
  };

  // Créer un nouvel administrateur
  const handleCreate = async () => {
    if (!fullname || !email || !phone || !role) {
      message.error("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin-management/admin-create`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fullname,
            email,
            phone,
            password,
            role,
            is_active: isActive ? 1 : 0,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erreur lors de la création");
      }

      message.success("Administrateur créé avec succès");
      resetCreateForm();
      fetchAdmins();
      setActiveTab("list");
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Modifier un administrateur
  const handleUpdate = async () => {
    if (!editFullname || !editEmail || !editPhone || !editRole) {
      message.error("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin-management/${editingAdmin.id}/admin-update`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fullname: editFullname,
            email: editEmail,
            phone: editPhone,
            role: editRole,
            is_active: editIsActive ? 1 : 0,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erreur lors de la modification");
      }

      message.success("Administrateur modifié avec succès");
      resetEditForm();
      fetchAdmins();
      setActiveTab("list");
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Changer le mot de passe
  const handlePasswordChange = async () => {
    if (!passwordAdminId) {
      message.error("Veuillez sélectionner un administrateur");
      return;
    }

    if (!newPassword) {
      message.error("Veuillez entrer un mot de passe");
      return;
    }

    if (newPassword.length < 6) {
      message.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    if (newPassword !== confirmPassword) {
      message.error("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin-management/${passwordAdminId}/admin-change-password`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ password: newPassword }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Erreur lors du changement de mot de passe"
        );
      }

      message.success("Mot de passe modifié avec succès");
      resetPasswordForm();
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Supprimer un administrateur
  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin-management/${id}/admin-delete`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors de la suppression");
      }

      message.success("Administrateur supprimé avec succès");
      fetchAdmins();
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Préparer l'édition
  const prepareEdit = (record) => {
    setEditingAdmin(record);
    setEditFullname(record.fullname);
    setEditEmail(record.email);
    setEditPhone(record.phone);
    setEditRole(record.role);
    setEditIsActive(record.is_active === 1);
    setActiveTab("edit");
  };

  // Préparer le changement de mot de passe
  const preparePasswordChange = (record) => {
    setPasswordAdminId(record.id);
    setSelectedAdminForPassword(record);
    setActiveTab("password");
  };

  const columns = [
    {
      title: "Nom complet",
      dataIndex: "fullname",
      key: "fullname",
      render: (text) => (
        <Space>
          <UserOutlined />
          <strong>{text}</strong>
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => (
        <Space>
          <MailOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: "Téléphone",
      dataIndex: "phone",
      key: "phone",
      render: (text) => (
        <Space>
          <PhoneOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: "Rôle",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Tag color={getRoleColor(role)}>{getRoleLabel(role)}</Tag>
      ),
    },
    {
      title: "Statut",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) => (
        <Badge
          status={isActive === 1 ? "success" : "error"}
          text={isActive === 1 ? "Actif" : "Inactif"}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Modifier">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => prepareEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Changer le mot de passe">
            <Button
              icon={<KeyOutlined />}
              size="small"
              onClick={() => preparePasswordChange(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Supprimer cet administrateur ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Tooltip title="Supprimer">
              <Button danger icon={<DeleteOutlined />} size="small" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!isSuperAdmin) {
    return (
      <div style={{ padding: "24px" }}>
        <Card>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Title level={3}>Accès Refusé</Title>
            <p>Seul le super administrateur peut accéder à cette page.</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Title level={2} style={{ marginBottom: "24px" }}>
              Gestion des Administrateurs
            </Title>

            <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
              {/* Onglet Liste */}
              <TabPane
                tab={
                  <span>
                    <UnorderedListOutlined />
                    Liste des Administrateurs
                  </span>
                }
                key="list"
              >
                <Table
                  columns={columns}
                  dataSource={admins}
                  loading={loading}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total: ${total} administrateurs`,
                  }}
                />
              </TabPane>

              {/* Onglet Créer */}
              <TabPane
                tab={
                  <span>
                    <UserAddOutlined />
                    Créer un Administrateur
                  </span>
                }
                key="create"
              >
                <div style={{ maxWidth: "600px", margin: "0 auto" }}>
                  <Card title="Nouveau Administrateur" bordered={false}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "500",
                          }}
                        >
                          Nom complet <span style={{ color: "red" }}>*</span>
                        </label>
                        <Input
                          prefix={<UserOutlined />}
                          placeholder="Jean Othniel"
                          value={fullname}
                          onChange={(e) => setFullname(e.target.value)}
                          size="large"
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "500",
                          }}
                        >
                          Email <span style={{ color: "red" }}>*</span>
                        </label>
                        <Input
                          prefix={<MailOutlined />}
                          type="email"
                          placeholder="othniel@gmail.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          size="large"
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "500",
                          }}
                        >
                          Téléphone <span style={{ color: "red" }}>*</span>
                        </label>
                        <Input
                          prefix={<PhoneOutlined />}
                          placeholder="0505055050"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          size="large"
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "500",
                          }}
                        >
                          Mot de passe <span style={{ color: "red" }}>*</span>
                        </label>
                        <Input
                          prefix={<UsbOutlined />}
                          placeholder="mot de passe*"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          size="large"
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "500",
                          }}
                        >
                          Rôle <span style={{ color: "red" }}>*</span>
                        </label>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Sélectionner un rôle"
                          value={role}
                          onChange={(value) => setRole(value)}
                          size="large"
                        >
                          {roles.map((r) => (
                            <Option key={r.value} value={r.value}>
                              <Tag color={r.color}>{r.label}</Tag>
                            </Option>
                          ))}
                        </Select>
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "500",
                          }}
                        >
                          Statut
                        </label>
                        <Switch
                          checked={isActive}
                          onChange={setIsActive}
                          checkedChildren="Actif"
                          unCheckedChildren="Inactif"
                        />
                      </div>

                      <div
                        style={{
                          marginTop: "24px",
                          display: "flex",
                          gap: "12px",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button size="large" onClick={resetCreateForm}>
                          Réinitialiser
                        </Button>
                        <Button
                          type="primary"
                          size="large"
                          icon={<PlusOutlined />}
                          onClick={handleCreate}
                          loading={loading}
                        >
                          Créer l'Administrateur
                        </Button>
                      </div>
                    </div>
                  </Card>
                </div>
              </TabPane>

              {/* Onglet Modifier */}
              <TabPane
                tab={
                  <span>
                    <EditOutlined />
                    Modifier un Administrateur
                  </span>
                }
                key="edit"
              >
                <div style={{ maxWidth: "600px", margin: "0 auto" }}>
                  {editingAdmin ? (
                    <Card
                      title={`Modifier: ${editingAdmin.fullname}`}
                      bordered={false}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "20px",
                        }}
                      >
                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "8px",
                              fontWeight: "500",
                            }}
                          >
                            Nom complet <span style={{ color: "red" }}>*</span>
                          </label>
                          <Input
                            prefix={<UserOutlined />}
                            placeholder="Jean Othniel"
                            value={editFullname}
                            onChange={(e) => setEditFullname(e.target.value)}
                            size="large"
                          />
                        </div>

                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "8px",
                              fontWeight: "500",
                            }}
                          >
                            Email <span style={{ color: "red" }}>*</span>
                          </label>
                          <Input
                            prefix={<MailOutlined />}
                            type="email"
                            placeholder="othniel@gmail.com"
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                            size="large"
                          />
                        </div>

                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "8px",
                              fontWeight: "500",
                            }}
                          >
                            Téléphone <span style={{ color: "red" }}>*</span>
                          </label>
                          <Input
                            prefix={<PhoneOutlined />}
                            placeholder="0505055050"
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.target.value)}
                            size="large"
                          />
                        </div>

                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "8px",
                              fontWeight: "500",
                            }}
                          >
                            Rôle <span style={{ color: "red" }}>*</span>
                          </label>
                          <Select
                            style={{ width: "100%" }}
                            placeholder="Sélectionner un rôle"
                            value={editRole}
                            onChange={(value) => setEditRole(value)}
                            size="large"
                          >
                            {roles.map((r) => (
                              <Option key={r.value} value={r.value}>
                                <Tag color={r.color}>{r.label}</Tag>
                              </Option>
                            ))}
                          </Select>
                        </div>

                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "8px",
                              fontWeight: "500",
                            }}
                          >
                            Statut
                          </label>
                          <Switch
                            checked={editIsActive}
                            onChange={setEditIsActive}
                            checkedChildren="Actif"
                            unCheckedChildren="Inactif"
                          />
                        </div>

                        <div
                          style={{
                            marginTop: "24px",
                            display: "flex",
                            gap: "12px",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button size="large" onClick={resetEditForm}>
                            Annuler
                          </Button>
                          <Button
                            type="primary"
                            size="large"
                            icon={<EditOutlined />}
                            onClick={handleUpdate}
                            loading={loading}
                          >
                            Enregistrer les modifications
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ) : (
                    <Card>
                      <div style={{ textAlign: "center", padding: "40px" }}>
                        <EditOutlined
                          style={{
                            fontSize: "48px",
                            color: "#bbb",
                            marginBottom: "16px",
                          }}
                        />
                        <p style={{ color: "#999" }}>
                          Sélectionnez un administrateur dans la liste pour le
                          modifier
                        </p>
                        <Button
                          type="primary"
                          onClick={() => setActiveTab("list")}
                        >
                          Voir la liste
                        </Button>
                      </div>
                    </Card>
                  )}
                </div>
              </TabPane>

              {/* Onglet Changer mot de passe */}
              <TabPane
                tab={
                  <span>
                    <KeyOutlined />
                    Changer le Mot de Passe
                  </span>
                }
                key="password"
              >
                <div style={{ maxWidth: "600px", margin: "0 auto" }}>
                  <Card
                    title={
                      selectedAdminForPassword
                        ? `Changer le mot de passe: ${selectedAdminForPassword.fullname}`
                        : "Changer le Mot de Passe"
                    }
                    bordered={false}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "500",
                          }}
                        >
                          Sélectionner un administrateur{" "}
                          <span style={{ color: "red" }}>*</span>
                        </label>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Choisir un administrateur"
                          value={passwordAdminId}
                          onChange={(value) => {
                            setPasswordAdminId(value);
                            const admin = admins.find((a) => a.id === value);
                            setSelectedAdminForPassword(admin);
                          }}
                          size="large"
                          showSearch
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {admins.map((admin) => (
                            <Option key={admin.id} value={admin.id}>
                              {admin.fullname} - {admin.email}
                            </Option>
                          ))}
                        </Select>
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "500",
                          }}
                        >
                          Nouveau mot de passe{" "}
                          <span style={{ color: "red" }}>*</span>
                        </label>
                        <Input.Password
                          prefix={<LockOutlined />}
                          placeholder="Minimum 6 caractères"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          size="large"
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "500",
                          }}
                        >
                          Confirmer le mot de passe{" "}
                          <span style={{ color: "red" }}>*</span>
                        </label>
                        <Input.Password
                          prefix={<LockOutlined />}
                          placeholder="Confirmer le mot de passe"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          size="large"
                        />
                      </div>

                      <div
                        style={{
                          marginTop: "24px",
                          display: "flex",
                          gap: "12px",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button size="large" onClick={resetPasswordForm}>
                          Réinitialiser
                        </Button>
                        <Button
                          type="primary"
                          size="large"
                          icon={<KeyOutlined />}
                          onClick={handlePasswordChange}
                          loading={loading}
                        >
                          Changer le Mot de Passe
                        </Button>
                      </div>
                    </div>
                  </Card>
                </div>
              </TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminManagement;
